// components/InlineOutreach.js
// Gmail-style outreach controls rendered INLINE under a completed shortlist in the chat.
// Select all / per-row checkboxes, bulk Draft / Send / Queue (auto-send on the hourly
// scheduler), a manual instruction for the drafting agent, and per-row edit/send.
// Reuses the existing outreach backend (shortlist → draft → approve → send/queue).
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Send, Zap, Sparkles, ChevronDown, ChevronRight, RefreshCw,
  AlertTriangle, CheckSquare, Square, Loader, Calendar, ExternalLink
} from 'lucide-react';
import {
  fetchShortlist, draftOutreach, updateOutreach, sendOutreach,
  queueOutreach, listOutreach, backgroundStatus,
  updateBackgroundSettings, proposeMeeting, sendMeetingProposal, bookMeeting
} from '../utils/outreach-api';
import { connectGmail, isGmailConnected, ensureGmailToken } from '../utils/gmail-connect';

const STATUS_META = {
  none: { label: 'Not drafted', color: '#8a8a82', bg: '#f0efe9' },
  draft: { label: 'Draft', color: '#8a6d3b', bg: '#fbf3e0' },
  approved: { label: 'Approved', color: '#2e7d4f', bg: '#e7f5ec' },
  gmail_draft: { label: 'In Gmail', color: '#2e7d4f', bg: '#e7f5ec' },
  queued: { label: 'Queued', color: '#3a6df0', bg: '#e9f0ff' },
  sent: { label: 'Sent', color: '#2e7d4f', bg: '#e7f5ec' },
  replied: { label: 'Replied', color: '#1f6feb', bg: '#e9f0ff' },
  bounced: { label: 'Bounced', color: '#c0392b', bg: '#fbeae8' },
  failed: { label: 'Failed', color: '#c0392b', bg: '#fbeae8' },
  skipped: { label: 'Skipped', color: '#8a8a82', bg: '#f0efe9' }
};

const keyOf = (c) => (c.email || c.name || '').toLowerCase();
const normName = (n) => String(n || '').trim().toLowerCase().replace(/\s+/g, ' ');
const browserTz = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch (e) { return 'UTC'; }
};
const defaultCalendar = () => ({
  timezone: browserTz(),
  slotMinutes: 30,
  workStartHour: 9,
  workEndHour: 17,
  searchDays: 6,
  leadHours: 18,
  slotCount: 3,
  allowWeekends: false
});
const calendarSummary = (cal = {}) => {
  const c = { ...defaultCalendar(), ...(cal || {}) };
  return `${c.slotMinutes}m, ${c.workStartHour}:00-${c.workEndHour}:00, ${c.slotCount} slots over ${c.searchDays} days`;
};

// Names the report actually shortlisted, e.g. "Shortlist 1: Jack Haynie".
function extractReportNames(text) {
  const set = new Set();
  if (!text) return set;
  const re = /Shortlist\s*\d+\s*:\s*([^\n|]+)/gi;
  let m;
  while ((m = re.exec(text))) set.add(normName(m[1]));
  return set;
}

export default function InlineOutreach({ roleQuery, roleTitle = '', candidates: providedCandidates = null, reportText = '' }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [rows, setRows] = useState([]); // [{ cand, record|null }]
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [edits, setEdits] = useState({}); // email -> {subject, body}
  const [meet, setMeet] = useState({}); // email -> { slots:[], proposal:{subject,body} }
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [calendarDraft, setCalendarDraft] = useState(defaultCalendar);
  const [instruction, setInstruction] = useState('');
  const [bg, setBg] = useState(null);
  const [gmailOn, setGmailOn] = useState(isGmailConnected());
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [recruiter, setRecruiter] = useState(() => {
    try { return JSON.parse(localStorage.getItem('outreachRecruiter')) || { name: '', org: '', address: '' }; }
    catch { return { name: '', org: '', address: '' }; }
  });

  const flash = (text, kind = 'info') => { setMsg({ text, kind }); setTimeout(() => setMsg(null), 6000); };
  const persistRecruiter = (r) => { setRecruiter(r); try { localStorage.setItem('outreachRecruiter', JSON.stringify(r)); } catch (e) { /* noop */ } };

  const load = useCallback(async () => {
    setBusy('load');
    try {
      const [existing, b] = await Promise.all([
        listOutreach().catch(() => []),
        backgroundStatus().catch(() => null)
      ]);
      const byEmail = {};
      (existing || []).forEach((r) => { if (r.candidate?.email) byEmail[r.candidate.email.toLowerCase()] = r; });

      // Prefer the EXACT candidates the report covered (passed from the search stream). Fall back
      // to a fresh shortlist for older chats, but filter it to only the names the report listed —
      // so the panel never shows people who weren't in the shortlist.
      let cands;
      if (Array.isArray(providedCandidates) && providedCandidates.length) {
        cands = providedCandidates;
      } else {
        const sl = await fetchShortlist(roleQuery, 15, false);
        cands = sl.candidates || [];
        const names = extractReportNames(reportText);
        if (names.size) cands = cands.filter((c) => names.has(normName(c.name)));
      }

      setRows(cands.map((c) => ({ cand: c, record: byEmail[(c.email || '').toLowerCase()] || null })));
      setBg(b);
      if (b?.settings?.calendar) setCalendarDraft({ ...defaultCalendar(), ...b.settings.calendar, timezone: b.settings.calendar.timezone || browserTz() });
      setLoaded(true);
    } catch (e) { flash(e.message || 'Could not load the shortlist.', 'error'); }
    setBusy('');
  }, [roleQuery, providedCandidates, reportText]);

  const openPanel = () => { setOpen(true); if (!loaded) load(); };

  const selectableKeys = rows.filter((r) => r.cand.email).map((r) => keyOf(r.cand));
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selected.has(k));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableKeys));
  const toggle = (k) => setSelected((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleExpanded = (k) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    return next;
  });
  const openDraft = (k) => setExpanded((prev) => {
    const next = new Set(prev);
    next.add(k);
    return next;
  });
  const draftedKeys = () => rows.filter((r) => r.record).map((r) => keyOf(r.cand));
  const openAllDrafts = () => setExpanded(new Set(draftedKeys()));
  const closeAllDrafts = () => setExpanded(new Set());
  const selectedRows = () => rows.filter((r) => selected.has(keyOf(r.cand)));

  const mergeRecords = (records) => {
    const byEmail = {};
    (records || []).forEach((r) => { if (r.candidate?.email) byEmail[r.candidate.email.toLowerCase()] = r; });
    setRows((prev) => prev.map((r) => byEmail[keyOf(r.cand)] ? { ...r, record: byEmail[keyOf(r.cand)] } : r));
  };
  const setOneRecord = (rec) => setRows((prev) => prev.map((r) => keyOf(r.cand) === (rec.candidate?.email || '').toLowerCase() ? { ...r, record: rec } : r));

  // --- Bulk + per-row actions ---
  const draftRows = async (targets) => {
    const withEmail = targets.filter((r) => r.cand.email);
    if (!withEmail.length) return flash('Select candidates that have an email first.', 'error');
    setBusy('draft');
    try {
      const { records } = await draftOutreach({ candidates: withEmail.map((t) => t.cand), roleQuery, roleTitle, recruiter, instruction });
      mergeRecords(records);
      flash(`Drafted ${records?.length || 0} email(s) with Claude${instruction ? ' (your instruction applied)' : ''}.`, 'success');
    } catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  const ensureApproved = async (rec, localEdit) => {
    const patch = { approve: true };
    if (localEdit?.subject != null) patch.subject = localEdit.subject;
    if (localEdit?.body != null) patch.body = localEdit.body;
    const updated = await updateOutreach(rec._id, patch);
    setOneRecord(updated);
    return updated;
  };

  const sendRows = async (targets) => {
    const drafted = targets.filter((r) => r.record && ['draft', 'approved', 'gmail_draft'].includes(r.record.status));
    if (!drafted.length) return flash('Generate drafts for the selected candidates first.', 'error');
    if (!recruiter.address?.trim()) return flash('Add your business mailing address below (legally required to send).', 'error');
    setBusy('send');
    try {
      const token = await ensureGmailToken();
      setGmailOn(true);
      let sent = 0;
      for (const r of drafted) {
        try {
          if (r.record.status === 'draft') await ensureApproved(r.record, edits[keyOf(r.cand)]);
          const out = await sendOutreach(r.record._id, token, recruiter);
          setOneRecord(out);
          sent += 1;
          if (drafted.length > 1) await new Promise((res) => setTimeout(res, 9000)); // paced for deliverability
        } catch (e) { flash(`${r.cand.name}: ${e.message}`, 'error'); }
      }
      if (sent) flash(`Sent ${sent} email(s).`, 'success');
    } catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  const queueRows = async (targets) => {
    const drafted = targets.filter((r) => r.record && ['draft', 'approved', 'gmail_draft'].includes(r.record.status));
    if (!drafted.length) return flash('Generate drafts first, then queue them.', 'error');
    if (!bg?.connected) return flash('Connect background Gmail in Outreach settings to auto-send on a schedule.', 'error');
    if (!recruiter.address?.trim()) return flash('Add your business mailing address below first.', 'error');
    setBusy('queue');
    try {
      let n = 0;
      for (const r of drafted) {
        if (r.record.status === 'draft') await ensureApproved(r.record, edits[keyOf(r.cand)]);
        const out = await queueOutreach(r.record._id, { recruiter });
        if (out?.record) setOneRecord(out.record);
        n += 1;
      }
      flash(`Queued ${n} for background auto-send (every ${bg.settings?.syncIntervalHours || 1}h). Replies sync and follow-ups fire automatically.`, 'success');
    } catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  const saveEdit = async (r) => {
    const e = edits[keyOf(r.cand)] || {};
    setBusy('edit');
    try {
      const updated = await updateOutreach(r.record._id, { subject: e.subject ?? r.record.subject, body: e.body ?? r.record.body });
      setOneRecord(updated);
      flash('Draft updated.', 'success');
    } catch (err) { flash(err.message, 'error'); }
    setBusy('');
  };

  const connect = async () => { try { await connectGmail(); setGmailOn(true); flash('Gmail connected.', 'success'); } catch (e) { flash(e.message, 'error'); } };

  const saveCalendarDefaults = async () => {
    setBusy('calendar-settings');
    try {
      const clean = {
        ...calendarDraft,
        timezone: calendarDraft.timezone || browserTz(),
        slotMinutes: Number(calendarDraft.slotMinutes) || 30,
        workStartHour: Number(calendarDraft.workStartHour) || 9,
        workEndHour: Number(calendarDraft.workEndHour) || 17,
        searchDays: Number(calendarDraft.searchDays) || 6,
        leadHours: Number(calendarDraft.leadHours) || 18,
        slotCount: Number(calendarDraft.slotCount) || 3,
        allowWeekends: !!calendarDraft.allowWeekends
      };
      await updateBackgroundSettings({ calendar: clean });
      const b = await backgroundStatus().catch(() => null);
      setBg(b);
      if (b?.settings?.calendar) setCalendarDraft({ ...defaultCalendar(), ...b.settings.calendar, timezone: b.settings.calendar.timezone || browserTz() });
      flash('Meeting defaults saved.', 'success');
    } catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  // --- Meeting scheduling (propose → confirm → book) ---
  const meetingSettings = () => ({
    ...defaultCalendar(),
    ...(bg?.settings?.calendar || {}),
    ...calendarDraft,
    timezone: calendarDraft.timezone || bg?.settings?.calendar?.timezone || browserTz()
  });

  const doPropose = async (r) => {
    const k = keyOf(r.cand);
    setBusy(`propose:${k}`);
    try {
      const res = await proposeMeeting(r.record._id, meetingSettings());
      setMeet((p) => ({ ...p, [k]: { slots: res.slots || [], proposal: res.proposal || {}, preferences: res.preferences || meetingSettings() } }));
      openDraft(k);
      flash(`Found ${res.slots?.length || 0} open slot(s) using ${calendarSummary(res.preferences || meetingSettings())}.`, 'success');
    } catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  const doSendProposal = async (r) => {
    const k = keyOf(r.cand);
    const m = meet[k];
    if (!recruiter.address?.trim()) return flash('Add your business mailing address below first.', 'error');
    setBusy(`sendprop:${k}`);
    try {
      const token = await ensureGmailToken();
      setGmailOn(true);
      const out = await sendMeetingProposal(r.record._id, { googleAccessToken: token, subject: m?.proposal?.subject, body: m?.proposal?.body, recruiter });
      if (out?.record) setOneRecord(out.record);
      flash('Proposal sent. Once they pick a time, click Book to add it to your calendar.', 'success');
    } catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  const doBook = async (r, slotIndex) => {
    const k = keyOf(r.cand);
    setBusy(`book:${k}`);
    try {
      const out = await bookMeeting(r.record._id, { slotIndex, recruiter });
      if (out?.record) setOneRecord(out.record);
      flash('Booked — calendar invite sent to the candidate.', 'success');
    } catch (e) { flash(e.message, 'error'); }
    setBusy('');
  };

  // --- Render ---
  const candCount = rows.length;
  const emailCount = rows.filter((r) => r.cand.email).length;
  const selCount = selected.size;

  if (!open) {
    return (
      <div style={st.collapsed}>
        <Mail size={15} />
        <span style={{ fontWeight: 600 }}>
          Email {Array.isArray(providedCandidates) && providedCandidates.length ? `these ${providedCandidates.length} shortlisted` : 'the shortlisted'} candidates
        </span>
        <span style={st.muted}>Draft, send & auto-follow-up — right here</span>
        <button style={st.primary} onClick={openPanel}>Open outreach</button>
      </div>
    );
  }

  return (
    <div style={st.wrap}>
      <div style={st.head}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 700 }}>
          <Mail size={16} /> Outreach
        </span>
        <span style={st.muted}>
          {candCount} candidate{candCount !== 1 ? 's' : ''}
          {candCount !== emailCount ? ` · ${emailCount} with email` : ''}
        </span>
        <span style={{ flex: 1 }} />
        {bg?.connected
          ? <span style={st.bgChip} title={`Background auto-runs every ${bg.settings?.syncIntervalHours || 1}h`}><Zap size={12} /> Auto-sync every {bg.settings?.syncIntervalHours || 1}h</span>
          : <button style={st.link} onClick={() => navigate('/outreach')}>Set up background sync</button>}
        <span style={{ ...st.dot, background: gmailOn ? '#2e9e5b' : '#c0392b' }} />
        {gmailOn ? 'Gmail on' : <button style={st.link} onClick={connect}>Connect Gmail</button>}
      </div>

      {msg && <div style={{ ...st.flash, ...(msg.kind === 'error' ? st.flashErr : msg.kind === 'success' ? st.flashOk : {}) }}>{msg.text}</div>}

      {/* Manual instruction for the drafting agent */}
      <div style={st.instrRow}>
        <Sparkles size={14} color="#c2613f" />
        <input
          style={st.instrInput}
          placeholder="Optional instruction for the AI (e.g. “mention our remote policy, keep it under 90 words”)"
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
        />
      </div>

      <div style={st.meetingDefaults}>
        <button style={st.link} onClick={() => setSettingsOpen((v) => !v)}>
          {settingsOpen ? 'Hide meeting defaults' : 'Meeting defaults'}
        </button>
        <span style={st.muted}>{calendarSummary(calendarDraft)}</span>
        {!bg?.connected && <span style={st.warnText}><AlertTriangle size={12} /> connect background Gmail for calendar booking</span>}
      </div>

      {settingsOpen && (
        <div style={st.meetingSettings}>
          <label style={st.fieldLabel}>Duration
            <select style={st.select} value={calendarDraft.slotMinutes} onChange={(e) => setCalendarDraft((p) => ({ ...p, slotMinutes: Number(e.target.value) }))}>
              {[15, 30, 45, 60, 90].map((m) => <option key={m} value={m}>{m} min</option>)}
            </select>
          </label>
          <label style={st.fieldLabel}>Work hours
            <span style={st.inlineFields}>
              <input style={st.smallInput} type="number" min="0" max="23" value={calendarDraft.workStartHour} onChange={(e) => setCalendarDraft((p) => ({ ...p, workStartHour: Number(e.target.value) }))} />
              <span style={st.muted}>to</span>
              <input style={st.smallInput} type="number" min="1" max="24" value={calendarDraft.workEndHour} onChange={(e) => setCalendarDraft((p) => ({ ...p, workEndHour: Number(e.target.value) }))} />
            </span>
          </label>
          <label style={st.fieldLabel}>Search days
            <input style={st.smallInput} type="number" min="1" max="21" value={calendarDraft.searchDays} onChange={(e) => setCalendarDraft((p) => ({ ...p, searchDays: Number(e.target.value) }))} />
          </label>
          <label style={st.fieldLabel}>Slots
            <input style={st.smallInput} type="number" min="1" max="8" value={calendarDraft.slotCount} onChange={(e) => setCalendarDraft((p) => ({ ...p, slotCount: Number(e.target.value) }))} />
          </label>
          <label style={st.fieldLabel}>Timezone
            <input style={st.tzInput} value={calendarDraft.timezone} onChange={(e) => setCalendarDraft((p) => ({ ...p, timezone: e.target.value }))} />
          </label>
          <label style={st.checkLabel}>
            <input type="checkbox" checked={!!calendarDraft.allowWeekends} onChange={(e) => setCalendarDraft((p) => ({ ...p, allowWeekends: e.target.checked }))} />
            include weekends
          </label>
          <button style={st.smallBtn} onClick={saveCalendarDefaults} disabled={busy === 'calendar-settings'}>
            {busy === 'calendar-settings' ? 'Saving...' : 'Save defaults'}
          </button>
        </div>
      )}

      {/* Gmail-style bulk toolbar */}
      <div style={st.toolbar}>
        <button style={st.checkBtn} onClick={toggleAll} title="Select all">
          {allSelected ? <CheckSquare size={18} color="#c2613f" /> : <Square size={18} color="#9a9a90" />}
        </button>
        <span style={st.muted}>{selCount ? `${selCount} selected` : 'Select all'}</span>
        <span style={{ width: 1, height: 20, background: '#e3e1d8', margin: '0 4px' }} />
        <button style={st.tbBtn} disabled={!selCount || busy === 'draft'} onClick={() => draftRows(selectedRows())}>
          {busy === 'draft' ? <Loader size={14} className="spin" /> : <Sparkles size={14} />} Draft
        </button>
        <button style={st.tbBtn} disabled={!selCount || busy === 'send'} onClick={() => sendRows(selectedRows())}>
          {busy === 'send' ? <Loader size={14} /> : <Send size={14} />} Send now
        </button>
        <button style={st.tbBtn} disabled={!selCount || busy === 'queue'} onClick={() => queueRows(selectedRows())}>
          <Zap size={14} /> Queue auto-send
        </button>
        <button style={st.tbBtn} disabled={!draftedKeys().length} onClick={openAllDrafts}>
          Open all drafts
        </button>
        <button style={st.tbBtn} disabled={!expanded.size} onClick={closeAllDrafts}>
          Close drafts
        </button>
        <span style={{ flex: 1 }} />
        <button style={st.iconBtn} title="Reload" onClick={load} disabled={busy === 'load'}><RefreshCw size={14} /></button>
      </div>

      {/* Rows */}
      <div style={st.list}>
        {busy === 'load' && <div style={st.empty}>Loading shortlist…</div>}
        {loaded && rows.length === 0 && <div style={st.empty}>No shortlisted candidates found for this search.</div>}
        {rows.map((r) => {
          const k = keyOf(r.cand);
          const status = r.record?.status || 'none';
          const meta = STATUS_META[status] || STATUS_META.none;
          const isOpen = expanded.has(k);
          const ed = edits[k] || {};
          return (
            <div key={k} style={{ ...st.row, ...(selected.has(k) ? st.rowSel : {}) }}>
              <div style={st.rowMain}>
                <button style={st.checkBtn} disabled={!r.cand.email} onClick={() => toggle(k)}>
                  {selected.has(k) ? <CheckSquare size={17} color="#c2613f" /> : <Square size={17} color={r.cand.email ? '#9a9a90' : '#d8d8d0'} />}
                </button>
                <div style={st.avatar}>{(r.cand.name || '?')[0].toUpperCase()}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={st.name}>{r.cand.name || 'Unknown'}</div>
                  <div style={st.sub}>
                    {[r.cand.firm, r.cand.office].filter(Boolean).join(' · ')}
                    {r.cand.email ? <span style={st.email}> · {r.cand.email}</span> : <span style={st.warnText}> · <AlertTriangle size={11} /> no email</span>}
                  </div>
                </div>
                <span style={{ ...st.statusChip, color: meta.color, background: meta.bg }}>{meta.label}</span>
                {status === 'replied' && r.record?.meeting?.status !== 'booked' && (
                  <button style={st.smallBtn} onClick={() => doPropose(r)} disabled={busy === `propose:${k}`}>
                    <Calendar size={13} /> {busy === `propose:${k}` ? 'Checking…' : 'Propose times'}
                  </button>
                )}
                {r.record?.meeting?.status === 'booked' && r.record.meeting.htmlLink && (
                  <a href={r.record.meeting.htmlLink} target="_blank" rel="noopener noreferrer" style={st.bookedChip}>
                    <Calendar size={12} /> Booked <ExternalLink size={11} />
                  </a>
                )}
                {r.record && (
                  <button style={st.iconBtn} title={isOpen ? 'Hide draft' : 'Show draft'} onClick={() => toggleExpanded(k)}>
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                )}
              </div>

              {isOpen && r.record && (
                <div style={st.editor}>
                  <input
                    style={st.subjInput}
                    value={ed.subject ?? r.record.subject ?? ''}
                    onChange={(e) => setEdits((p) => ({ ...p, [k]: { ...p[k], subject: e.target.value } }))}
                    placeholder="Subject"
                  />
                  <textarea
                    style={st.bodyInput}
                    value={ed.body ?? r.record.body ?? ''}
                    onChange={(e) => setEdits((p) => ({ ...p, [k]: { ...p[k], body: e.target.value } }))}
                    placeholder="Email body"
                  />
                  <div style={st.editActions}>
                    <button style={st.smallBtn} onClick={() => saveEdit(r)} disabled={busy === 'edit'}>Save edits</button>
                    <button style={st.smallBtn} onClick={() => sendRows([r])} disabled={busy === 'send'}><Send size={13} /> Send</button>
                    <button style={st.smallBtn} onClick={() => queueRows([r])} disabled={busy === 'queue'}><Zap size={13} /> Queue</button>
                  </div>

                  {/* Meeting proposal (after they reply 'interested') */}
                  {meet[k] && (
                    <div style={st.meetBox}>
                      <div style={st.meetHead}><Calendar size={13} /> Propose a call — review this reply, then send. You confirm the booking after they pick.</div>
                      <input
                        style={st.subjInput}
                        value={meet[k].proposal?.subject || ''}
                        onChange={(e) => setMeet((p) => ({ ...p, [k]: { ...p[k], proposal: { ...p[k].proposal, subject: e.target.value } } }))}
                      />
                      <textarea
                        style={{ ...st.bodyInput, minHeight: 110 }}
                        value={meet[k].proposal?.body || ''}
                        onChange={(e) => setMeet((p) => ({ ...p, [k]: { ...p[k], proposal: { ...p[k].proposal, body: e.target.value } } }))}
                      />
                      <div style={st.editActions}>
                        <button style={st.smallBtn} onClick={() => doSendProposal(r)} disabled={busy === `sendprop:${k}`}>
                          <Send size={13} /> {busy === `sendprop:${k}` ? 'Sending…' : 'Send proposal'}
                        </button>
                      </div>
                      <div style={st.meetSlots}>
                        <span style={st.muted}>When they confirm, book the slot:</span>
                        {(meet[k].slots || []).map((sl) => (
                          <button key={sl.index} style={st.slotBtn} onClick={() => doBook(r, sl.index)} disabled={busy === `book:${k}`}>
                            <Calendar size={12} /> {sl.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Sender details required for sending (CAN-SPAM) */}
      {!recruiter.address?.trim() && rows.length > 0 && (
        <div style={st.senderBox}>
          <div style={st.warnText}><AlertTriangle size={13} /> A business mailing address is legally required before sending. Set it once:</div>
          <div style={st.senderRow}>
            <input style={st.in} placeholder="Your name" value={recruiter.name} onChange={(e) => persistRecruiter({ ...recruiter, name: e.target.value })} />
            <input style={st.in} placeholder="Company / firm" value={recruiter.org} onChange={(e) => persistRecruiter({ ...recruiter, org: e.target.value })} />
            <input style={{ ...st.in, flex: 2 }} placeholder="Business mailing address" value={recruiter.address} onChange={(e) => persistRecruiter({ ...recruiter, address: e.target.value })} />
          </div>
        </div>
      )}
    </div>
  );
}

const st = {
  collapsed: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 14, padding: '10px 14px', background: '#fbfbfa', border: '1px solid #ececE6', borderRadius: 12, fontSize: 13.5, color: '#3d3d3a', fontFamily: '-apple-system, system-ui, "Segoe UI", Roboto, sans-serif' },
  primary: { marginLeft: 'auto', padding: '7px 14px', borderRadius: 8, border: 'none', background: '#c2613f', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  wrap: { marginTop: 14, border: '1px solid #e6e4da', borderRadius: 12, background: '#fff', overflow: 'hidden', fontFamily: '-apple-system, system-ui, "Segoe UI", Roboto, sans-serif', color: '#3d3d3a' },
  head: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#faf9f5', borderBottom: '1px solid #eceae1', fontSize: 13 },
  muted: { fontSize: 12.5, color: '#8a8a82' },
  bgChip: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, background: '#eef6f0', color: '#2e7d4f', fontSize: 12, fontWeight: 600, border: '1px solid #d4e9dc' },
  dot: { width: 8, height: 8, borderRadius: '50%', display: 'inline-block' },
  link: { background: 'transparent', border: 'none', color: '#3a6df0', fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline', padding: 0 },
  flash: { padding: '8px 14px', fontSize: 13, background: '#f4f3ee', borderBottom: '1px solid #eceae1' },
  flashErr: { background: '#fbeae8', color: '#9b2c22' },
  flashOk: { background: '#e7f5ec', color: '#1e6b41' },
  instrRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px 4px' },
  instrInput: { flex: 1, padding: '8px 10px', border: '1px solid #e3e1d8', borderRadius: 8, fontSize: 13, outline: 'none' },
  meetingDefaults: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '6px 14px 8px' },
  meetingSettings: { display: 'flex', alignItems: 'end', gap: 10, flexWrap: 'wrap', padding: '8px 14px 10px', background: '#fbfaf6', borderTop: '1px solid #f0eee6', borderBottom: '1px solid #eceae1' },
  fieldLabel: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, color: '#73736b', fontWeight: 700 },
  select: { minWidth: 90, padding: '6px 8px', border: '1px solid #e3e1d8', borderRadius: 7, fontSize: 12.5, background: '#fff' },
  smallInput: { width: 58, padding: '6px 7px', border: '1px solid #e3e1d8', borderRadius: 7, fontSize: 12.5 },
  tzInput: { width: 170, padding: '6px 7px', border: '1px solid #e3e1d8', borderRadius: 7, fontSize: 12.5 },
  inlineFields: { display: 'flex', alignItems: 'center', gap: 5 },
  checkLabel: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: '#73736b', fontWeight: 600, paddingBottom: 7 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderBottom: '1px solid #eceae1', flexWrap: 'wrap' },
  checkBtn: { background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center' },
  tbBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 8, border: '1px solid #e0ded3', background: '#fff', color: '#3d3d3a', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  iconBtn: { background: 'transparent', border: 'none', cursor: 'pointer', padding: 5, borderRadius: 6, color: '#73736b', display: 'flex', alignItems: 'center' },
  list: { maxHeight: 460, overflowY: 'auto' },
  empty: { padding: '18px 14px', fontSize: 13, color: '#9a9a90', textAlign: 'center' },
  row: { borderBottom: '1px solid #f1f0ea' },
  rowSel: { background: '#fdf6f2' },
  rowMain: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px' },
  avatar: { width: 28, height: 28, borderRadius: '50%', background: '#e8e7e1', color: '#73736b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 },
  name: { fontSize: 13.5, fontWeight: 600, color: '#2b2b27', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  sub: { fontSize: 12, color: '#8a8a82', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  email: { color: '#73736b' },
  warnText: { color: '#b9770e', display: 'inline-flex', alignItems: 'center', gap: 4 },
  statusChip: { fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap' },
  editor: { padding: '0 14px 12px 52px', display: 'flex', flexDirection: 'column', gap: 6 },
  subjInput: { padding: '7px 9px', border: '1px solid #e3e1d8', borderRadius: 7, fontSize: 13, fontWeight: 600, outline: 'none' },
  bodyInput: { padding: '8px 9px', border: '1px solid #e3e1d8', borderRadius: 7, fontSize: 13, lineHeight: 1.5, minHeight: 130, outline: 'none', resize: 'vertical', fontFamily: 'inherit' },
  editActions: { display: 'flex', gap: 8 },
  smallBtn: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 11px', borderRadius: 7, border: '1px solid #e0ded3', background: '#fff', color: '#3d3d3a', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
  bookedChip: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 999, background: '#e7f5ec', color: '#2e7d4f', fontSize: 11.5, fontWeight: 700, textDecoration: 'none', border: '1px solid #cfe9d9' },
  meetBox: { marginTop: 4, padding: '10px', border: '1px solid #e3e1d8', borderRadius: 9, background: '#fbfaf6', display: 'flex', flexDirection: 'column', gap: 6 },
  meetHead: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: '#73736b' },
  meetSlots: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 },
  slotBtn: { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 7, border: '1px solid #d4c3b8', background: '#fff', color: '#7a4a32', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  senderBox: { padding: '10px 14px', borderTop: '1px solid #eceae1', background: '#fbfaf6' },
  senderRow: { display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' },
  in: { flex: 1, minWidth: 120, padding: '7px 9px', border: '1px solid #e3e1d8', borderRadius: 7, fontSize: 13, outline: 'none' }
};
